import { captureProductEvent } from "@/lib/product-analytics";
import { cn } from "@/lib/utils";
import type { VideoPlacement } from "@types";
import { VIDEOS, youtubeThumbnailUrl, type VideoKey } from "@webapp/utils/videos";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";

// YouTube's player only loads on tap, so pages stay as light as a poster image.
// `cookie` uses youtube.com rather than youtube-nocookie.com so plays are
// attributed like any normal embed.
export function YouTubeVideo({
  videoKey,
  placement,
  className,
}: {
  videoKey: VideoKey;
  placement: VideoPlacement;
  className?: string;
}) {
  const video = VIDEOS[videoKey];
  const isShort = video.kind === "short";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl bg-black",
        // Capped by viewport height too, so a vertical Short never runs past a phone screen.
        isShort ? "mx-auto w-full max-w-[min(360px,calc(75svh*9/16))]" : "w-full",
        className,
      )}
      data-testid={`youtube-video-${videoKey}`}
    >
      <LiteYouTubeEmbed
        id={video.youtubeId}
        title={video.title}
        cookie
        thumbnail={youtubeThumbnailUrl(videoKey)}
        aspectWidth={isShort ? 9 : 16}
        aspectHeight={isShort ? 16 : 9}
        // The library's stylesheet hard-codes `aspect-ratio: 16 / 9`, which
        // overrides the props above in every modern browser.
        style={{ aspectRatio: isShort ? "9 / 16" : "16 / 9" }}
        params="rel=0"
        onIframeAdded={() =>
          captureProductEvent("video_play_clicked", { video: videoKey, placement })
        }
      />
    </div>
  );
}
